import React, { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./style.module.scss";
import close from "@/assets/close.svg";
import { Icon } from "@/elements-react/fix-icon";
import { useIsMobile } from "@/util-react/useIsMobile";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  fullScreenMobileMode?: boolean;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, className = "", fullScreenMobileMode = false }: ModalProps) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  if (!isOpen) {
    return;
  }

  const modalContent = (
    <div className={`${isMobile && styles.mobile} ${styles.overlay} ${isOpen && styles.overlayVisible}`} onClick={handleOverlayClick}>
      <div
        className={`${styles.modal} ${className} ${isMobile && styles.mobile} ${fullScreenMobileMode && styles.fullScreenMobile}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={`${isMobile && styles.mobile} ${styles.closeButton}`} onClick={handleCloseClick} type="button">
          <Icon className={styles.closeIcon} src={close} />
        </button>
        <div className={`${isMobile && styles.mobile} ${styles.content}`}>{children}</div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
